/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import error from '../../../../common/nodejs/errors';
import config from '../../../../share/config';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';


/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  EXECUTE: 'execute',
  SUCCESS: 'success',
  FAILED: 'failed',
  ERROR: 'error'
};

/** API type定義 */
const API_TYPE = {
  START: 'start',
  TERMINATE: 'terminate',
};

export default {
  name: 'requirement062',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      nextPhase: '',
      modal_requirement_062: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.ERROR:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        case PHASE.EXECUTE:
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_062 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_062 = false;
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.ERROR;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** 初期化処理 */
    async startProcess() {
      // start実行
      this.nextPhase = '';
      await this.doStart();

      if (this.nextPhase !== '') {
        const ret = await this.doTerminate();
        if (ret) {
          this.phase = this.nextPhase;
        } else {
          this.showUnexpectedError();
        }
      } else {
        this.showUnexpectedError();
      }
    },

    /** 終了処理 */
    async exitProcess() {
      await this.doTerminate();
      this.closeWizard();
    },

    /** start実行 */
    async doStart() {
      try {
        this.isLoading = true;

        // 実行監視画面へ遷移
        this.phase = PHASE.EXECUTE;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.START
        };

        // start API実行
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了
              this.nextPhase = PHASE.SUCCESS;
              break;
            case config.STATUS_WSP_FAILED:
              switch (response.data.async_code) {
                case error.WSP_062_INITIALIZATION_FAILED:
                  // 独自エラー
                  this.nextPhase = PHASE.FAILED;
                  break;
                default:
                  break;
              }
              break;
            default:
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** terminate実行 */
    async doTerminate() {
      let ret = false;
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.TERMINATE
        };

        // terminate API実行
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了
              ret = true;
              break;
            case config.STATUS_WSP_FAILED:
            default:
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
      return ret;
    },

    // ユーザ操作
    /** 初期画面 OKボタン押下 */
    onStartOkClicked() {
      this.startProcess();
    },

    /** 初期画面 キャンセルボタン押下 */
    onStartCancelClicked() {
      this.closeWizard();
    },

    /** 正常終了画面 OKボタン押下 */
    onSuccessOkClicked() {
      this.closeWizard();
    },

    /** 失敗表示画面 OKボタン押下 */
    onFailedOkClicked() {
      this.closeWizard();
    },

    /** 共通エラー画面 OKボタン押下 */
    onErrorOkClicked() {
      this.exitProcess();
    }
  },

  async mounted() {
    this.init();
  },
};